/**
 * Extracts a readable item name from a slug.
 * Example: "hotel-mount-fuji" => "Hotel Mount Fuji"
 */
export const extractItemNameFromSlug = (slug: string): string => {
  if (!slug || typeof slug !== "string") {
    return "";
  }
  // Remove trailing slashes in a linear-time way to avoid regex backtracking
  const stripTrailingSlashes = (s: string) => {
    let end = s.length;
    while (end > 0 && s.charAt(end - 1) === "/") end -= 1;
    return s.slice(0, end);
  };

  const cleanSlug = stripTrailingSlashes(slug).trim();

  if (!cleanSlug) {
    return "";
  }

  return cleanSlug
    ?.split("-")
    ?.filter((word) => word && word.trim() !== "")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");
};

/**
 * Converts a name to a URL-friendly slug.
 * Removes "HOTEL" prefix if present and replaces spaces & invalid chars.
 */
export const convertNameToSlug = (name: string): string => {
  let processedName = name.trim();
  if (processedName.toUpperCase().startsWith("HOTEL")) {
    processedName = processedName.substring(6).trim();
  }
  return processedName
    .toLowerCase()
    .replaceAll(/\s+/g, "-")
    .replaceAll(/[^\w-]+/g, "")
    .replaceAll(/--+/g, "-");
};

// Generates query variable for filtering hotels by slug (contains) and optional documentId.
export const generateQueryVariableForHotelByItemName = <T>(
  slug: string,
  documentId: string = "",
): T => {
  return {
    slug: {
      containsi: slug,
    },
    ...(documentId
      ? {
        documentId: {
          eq: documentId,
        },
      }
      : {}),
  } as T;
};

// Generates query variable for filtering hotels whose slug ends with the given string.
export const generateQueryVariableForHotelByItemNameEndWith = <T>(
  slug: string,
): T => {
  return {
    slug: {
      endsWith: slug,
    },
  } as T;
};
export interface IVariableQueryHotelBySlug {
  slug?: {
    eq: string;
  };
  documentId?: {
    eq: string;
  };
}

export interface IVariableQueryCityBySlug {
  slug_hotel?: {
    eq: string;
  };
  documentId?: {
    eq: string;
  };
}

// Generates query variable for exact hotel slug match with optional documentId.
export const generateQueryVariableForHotelEqualSlug = <T>(
  slug?: string,
  documentId?: string,
): T | IVariableQueryHotelBySlug => {
  return {
    ...(slug
      ? {
        slug: {
          eq: slug,
        },
      }
      : {}),
    ...(documentId
      ? {
        documentId: {
          eq: documentId,
        },
      }
      : {}),
  } as T;
};

// Generates query variable for exact city slug match with optional documentId.
export const generateQueryVariableForCityEqualSlug = <T>(
  slug_hotel?: string,
  documentId?: string,
): T | IVariableQueryCityBySlug => {
  return {
    ...(slug_hotel
      ? {
        slug_hotel: {
          eq: slug_hotel,
        },
      }
      : {}),
    ...(documentId
      ? {
        documentId: {
          eq: documentId,
        },
      }
      : {}),
  } as T;
};

// Formats a date string to a human-readable format: "Month DD, YYYY"
export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "2-digit",
  };
  return date.toLocaleDateString("en-US", options);
};

// Returns the current date in Japan (Asia/Tokyo) in YYYY-MM-DD format.
export const getJapanDate = (): string => {
  const japanDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  return japanDate; // Format: YYYY-MM-DD
};

/**
 * Removes known hotel detail prefixes from a slug for cleaner identification.
 * Optionally supports a shadowPrefix for dynamic removal.
 */
export const removeSlugHotelDetailPrefix = (
  slug: string | null,
  shadowPrefix?: string,
): string => {
  if (!slug || typeof slug !== "string") return "";

  const cleanSlug = slug.replace(/\/+$/, "").toLowerCase();

  const knownPrefixes = [
    "rooms-",
    "location-",
    "attraction-",
    "banquets-",
    "restaurants-",
    "breakfast-",
    "facilities-",
    "onsen-",
    "pool-spa-",
    "faq-",
    "activity-",
  ];

  if (shadowPrefix) {
    knownPrefixes.push(shadowPrefix);
  }

  // Step 1: Known prefix removal
  for (const prefix of knownPrefixes) {
    if (cleanSlug.startsWith(prefix)) {
      return cleanSlug.substring(prefix.length);
    }
  }

  // Step 2: Extract "hotel-..." pattern safely
  const match = cleanSlug.match(/hotel-[a-z0-9-]+/);

  if (match) {
    return match[0];
  }

  // Step 3: fallback
  return cleanSlug;
};

export const slugifyItemName = (value: string): string => {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
};


export const isHotelDetailSubPage = (slug: string) => {
  const cleanedSlug = slug.startsWith("/")
    ? slug.slice(1)
    : slug;
  const patterns = [
    /^restaurants-.+\/.+$/,          
    /^banquets-.+\/.+$/,              
    /^activity-.+\/.+$/,              
    /^attraction-.+\/.+$/,             
    /^[a-zA-Z]+-hotel-.+\/party-.+$/,
  ];

  return patterns.some((regex) => regex.test(cleanedSlug));
}